/** The three ways a private is paid, as the server decides them (`PrivatePayment`, D-691). */
export type PaymentRoute = 'FromCredit' | 'ToTeacherAtLink' | 'ToTeacherUnknownHow'

import {
  addDays,
  addMonths,
  formatDayLabel,
  formatFullDate,
  formatRange,
  monthGrid,
  monthOf,
  startOfWeek,
  today,
} from './calendar'
import type { AttendanceState } from './attendance'
import type { ChoreoLink } from './calendar'

export type PortalDancer = { dancerId: string; firstName: string; name: string }

export type PortalGuardian = {
  name: string
  email: string | null
  mobilePhone: string | null
  relationship: string | null
}

export type PortalHousehold = {
  name: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  postalCode: string | null
}

export type PortalHome = {
  greeting: string
  /**
   * Today at the studio, from the server (D-68, D-384).
   *
   * The teacher payload always carried this; the portal did not, so the Today
   * panel fell back to `new Date().toISOString()` for every parent — a UTC
   * date, which rolls over at 6-7pm Central, inside studio hours. It would have
   * said tomorrow on a class night, for families only.
   */
  today: string
  dancers: PortalDancer[]
  household: PortalHousehold
  guardians: PortalGuardian[]
}

export type PortalSession = {
  occurrenceId: string
  studioClassId: string
  className: string
  date: string
  startTime: string | null
  endTime: string | null
  roomName: string | null
  /** The studio colour-codes its rooms; the portal reads the same value (D-176). */
  roomColor: string | null
  instructors: string[]
  state: AttendanceState
  isCancelled: boolean
  isNext: boolean
  /**
   * She is here to make up a class she missed, not because she is enrolled
   * (D-283). Always sent, so it is required — a fixture that omits it is
   * describing a server that does not exist.
   */
  isMakeup: boolean
  /** Taken against no absence, to spend later (D-611). */
  isBanked?: boolean
  /**
   * A level-up makeup her teacher has not answered yet (D-403).
   *
   * Shown rather than hidden, because she is holding a place - the owner chose
   * that asking takes the seat until it is answered. What the row must not do
   * is read as settled, which is what this marks.
   */
  awaitingTeacher?: boolean
  /** The class it repairs, so the row can say what it is for. */
  repairsClassName?: string | null
  /**
   * Where a booked one-off stands. Null on anything already running, which is
   * every technique class and every ordinary enrolment.
   */
  oneOffStatus?: OneOffStatus | null
  /**
   * The class's practice videos, in the order the routine goes (D-541).
   *
   * Unlisted YouTube links the teacher added, each with her label for which
   * part. Empty on a private booking, which belongs to no class, and never
   * sent to the classroom tablet.
   */
  choreoLinks?: ChoreoLink[]
  /**
   * The class's practice music, the same folder the classroom tablet opens
   * (D-119), now for the family at home as well (D-596). Absent on a private
   * booking, which belongs to no class.
   */
  musicUrl?: string | null
  /** A private lesson or choreography, rather than a class she is enrolled in. */
  isPrivate?: boolean
  /**
   * How this lesson gets paid for (D-691), or absent where nobody is billed —
   * a rehearsal, a room a teacher holds for herself (D-80), or a lesson still
   * waiting on her yes (D-509).
   *
   * <b>The server decides the branch, not this screen.</b> Derived here from
   * the link and a credit flag it was wrong on the day it was written: it told
   * a family holding credit to pay their teacher, contradicting D-662 and the
   * email that family had already been sent.
   */
  paymentRoute?: PaymentRoute | null
  /**
   * Where to pay her — sent only with `ToTeacherAtLink`, so a link can never
   * render under a sentence that contradicts it.
   */
  teacherPaymentLinks?: string[] | null
  /**
   * This family may call this one off themselves (D-438, D-493).
   *
   * **Answered by the server**, because two of the four refusals are invisible
   * here: whether a second household is on the booking — a duet is one booking
   * carrying two families, and neither may end the other's paid lesson — and
   * whether the kind is one a family pays for at all, since the portal labels
   * a rehearsal "Private with ..." like everything else.
   *
   * The 24-hour bound is in it too, so the button is not offered on a lesson
   * it could only refuse. It can go stale on a page left open all evening;
   * the endpoint's own refusal covers that, and that refusal is written to be
   * read rather than to be a wall.
   */
  mayCancel?: boolean
}

/**
 * Three states, because a booked one-off can be past as well as unfilled
 * (D-283). `DidNotRun` is the one two booleans could not say, and it is the one
 * that matters: the entitlement has already been handed back, so a row still
 * reading "may not run yet" contradicts the offer panel above it.
 */
export type OneOffStatus = 'Provisional' | 'Confirmed' | 'DidNotRun'

export type AttendanceStanding = {
  attended: number
  counted: number
  excused: number
  notYetMarked: number
  notHeld: number
  percentage: number | null
  isGated: boolean
  meetsThreshold: boolean
  hasGaps: boolean
  /** The percentage required, from settings. Never assumed by a screen (D-168). */
  threshold: number
  /**
   * The date attendance counts from, when one is set (D-591). Printed beside
   * the number so a grace week reads as policy rather than as a fault. The
   * server always sends it; optional here only so the many test fixtures that
   * build a standing by hand do not each have to say "no date".
   */
  countsFrom?: string | null
  /**
   * Good, under, or not yet (D-587). False `meetsThreshold` only ever goes
   * with `Under`; a dancer behind the floor is never below the line.
   */
  verdict?: StandingVerdict

  /**
   * Absences she still has time to make up, and which are therefore **not** in
   * the figures above (D-654).
   *
   * Zero for everybody but the company team, because only a deadline creates a
   * grace. Where there is one this has to be on screen: she reads 100% while a
   * clock runs, and a number nobody is warned about falling is worse than one
   * that was never flattering.
   */
  pendingRepair?: number
}

export type StandingVerdict = 'NotYet' | 'Good' | 'Under'

/**
 * The one colour the overall panel is (D-588): grey, green, amber for a class
 * under the line, red. From the server, so the panel and the Standing view
 * cannot disagree.
 */
export type OverallState = 'NotYet' | 'Good' | 'ClassUnder' | 'Under'

/** "2026-09-14" -> "14 Sep": the form the panel prints a counting date in (D-591). */
export function shortDate(iso: string): string {
  const { day, month } = formatDayLabel(iso)
  return `${day} ${month}`
}

/**
 * Attendance in one class.
 *
 * **No `meetsThreshold`, deliberately** — the server does not send one (D-169).
 * Eligibility is a fact about a dancer, not about one of her classes, so there
 * is no field here for a screen to accidentally render a per-class pass or fail
 * out of. The parts sum to the whole; only the whole carries a verdict.
 */
export type ClassAttendance = {
  studioClassId: string
  className: string
  attended: number
  counted: number
  excused: number
  notYetMarked: number
  notHeld: number
  percentage: number | null
  hasGaps: boolean
  /**
   * The classes behind a requirement group (D-170), empty for an ordinary class.
   *
   * A row named "Level 2 tech" is otherwise a figure with no visible source, and
   * the first question it invites is which classes it covers.
   */
  alternatives: { studioClassId: string; name: string }[]
  /** How many of `alternatives` are required each week. 0 when not a group. */
  requiredPerWeek: number
  isGroup: boolean
  /**
   * This class's own verdict, behind the floor (D-587). A prompt to look,
   * never a gate: only the overall decides who competes.
   */
  verdict?: StandingVerdict
  /** Technique, choreo or recital, so two rows both reading "Jazz" can be told apart. */
  category?: 'Tech' | 'Choreo' | 'Recital' | null
  /**
   * Whether this row is inside the figure that gates competing (D-644).
   *
   * Choreography and recital rows are shown, and judged, and are not in the
   * total - so the rows no longer add up to the number above them unless you
   * add only these. The server decides it, which is why the screen can say so
   * rather than inferring it from `category` and drifting the day the rule
   * changes.
   */
  countsTowardEligibility?: boolean
}

/** The total, and the class rows behind it - the ones that count add up to it (D-644). */
export type DancerStanding = {
  overall: AttendanceStanding
  byClass: ClassAttendance[]
  /** The panel's one colour, from the server (D-588). */
  state?: OverallState
  /** The classes under the line by name, for the amber sentence. */
  classesUnder?: string[]
}

export type PortalDancerView = {
  dancerId: string
  name: string
  upcoming: PortalSession[]
  standing: DancerStanding
  /**
   * Her first class beyond the two-week window, when that window is empty.
   *
   * "Nothing scheduled" is true in August and reads as broken, because the
   * season has not started yet (D-176). Null when she is enrolled in nothing.
   */
  startsOn: string | null
  /** What the studio has on file for costumes (D-238). */
  measurements: PortalMeasurements
  /** Which days were missed, newest first — what a percentage cannot say (D-271). */
  missed: MissedSession[]
  /**
   * Whether a private for her comes out of credit on the account (D-662, D-836).
   *
   * **The server answers this and the screen never works it out** — the rule this
   * file states for `paymentRoute` below, for the same reason. It gates the
   * account-credit sentence on the private request form, which used to be shown to
   * every family whether or not she holds any.
   */
  paysFromCredit: boolean
}

/**
 * A session the dancer was not at (D-271).
 *
 * <b>`Excused` and `Unexcused` are different facts and only one counts.</b> An
 * accepted reason is excluded from the percentage entirely (PRD §7.6), so a
 * screen adding them together would print a total that contradicts the bar
 * above it. Use {@link missedCount} rather than `missed.length`.
 */
/** `Spent`: a makeup she booked and did not turn up to - used, and nothing else repairs the night (D-278). */
/** `Banked`: a class she took before this night, counted against it now (D-592). */
/** `Paired`: the other class in her counts-as-one group covered the week (D-788). */
export type NightRepairKind =
  | 'None'
  | 'Requested'
  | 'Booked'
  | 'MadeUp'
  | 'Spent'
  | 'Banked'
  | 'PrivateLesson'
  | 'Paired'

export type NightRepair = {
  kind: NightRepairKind
  /** The makeup's date, for Booked and MadeUp. */
  on: string | null
  /** The class she visits or visited, or the teacher a private is with or waiting on. */
  with: string | null
}

export type MissedSession = {
  occurrenceId: string
  className: string
  date: string
  state: 'Unexcused' | 'Excused'
  /**
   * Whether a private is what repairs this one (D-292, D-405).
   *
   * <b>Named for what it decides, not for what the class is.</b> It was
   * `isSoloDuetTrio`, which was the same answer for exactly as long as solos
   * were the only thing privates repaired, and stopped being the same answer
   * the moment group choreo joined them. A field named after the evidence goes
   * quietly stale when the rule moves; one named after the conclusion does not.
   *
   * A solo has no peer session and no band any rule can read, and a group
   * routine has no class that already runs which could stand in for it. A
   * technique class is the opposite, and keeps the scheduled makeup.
   *
   * Sent by the server rather than guessed from the name, which is the D-88
   * mistake.
   */
  repairedByPrivate: boolean
  /**
   * Whether this absence is repaired by anything at all (D-508).
   *
   * False for a recital class, which is recreational — measured but never
   * gated, so nothing is owed. Distinct from "no session matches yet", which
   * is what the screen said before and reads as a fault rather than a policy.
   */
  repairable: boolean
  /** Which class line this night sits under (D-589). */
  studioClassId?: string
  /**
   * What is repairing it, if anything (D-589). A made-up night stays on the
   * list and says so, where it used to vanish and leave the number unexplained.
   */
  repair?: NightRepair | null

  /**
   * The last day this night can be made up (D-655), or absent when there is no
   * deadline — which is every dancer outside the company team.
   *
   * **Distinct from `repairable`**, which says whether this *kind* of class can
   * be repaired at all. A night can be repairable in kind and out of time, and
   * the row has to say exactly that rather than offering a repair the server
   * would refuse.
   */
  makeUpBy?: string | null

  /**
   * What the teacher wrote for the family about this night (D-656).
   *
   * **The family half only.** `AttendanceRecord.Note` is the studio's own and
   * never enters this payload — it travels to every browser that opens the
   * panel, and a field a screen merely hides has still been sent.
   */
  familyNote?: string | null
  /**
   * A banked class that came close to covering this night and could not
   * (D-931, D-933).
   *
   * **Absent for a family, and the server is what withholds it** (G3): until
   * the teacher of the missed class can actually be asked, a parent reading
   * *a credit nearly covered this* has been told something nobody can act on.
   * So this is undefined on the portal's payload rather than hidden by the
   * screen.
   *
   * ⚠ **It is not a repair.** `repair.kind` stays `None`, the night still
   * counts against her, and it still appears in *to make up* — a near miss
   * explains why nothing covered the night, it does not cover it.
   */
  /**
   * Her attendance record for this night, which the excuse door is keyed on
   * (D-941). Absent for a family: the server withholds it rather than the
   * screen hiding it, because she cannot call that door.
   */
  absenceRecordId?: string | null
  nearlyCovered?: {
    className: string
    creditLevels: string
    nightLevels: string
    /** The session that earned the credit, named on the raise so the server can check it (D-935). */
    creditOccurrenceId: string
    /** Her attendance record for this night — the ask subject (D-936). */
    absenceRecordId: string
    /**
     * Where the question has got to, or absent if nobody has asked (D-938).
     *
     * There is no `Agreed`, because a yes is not an answer to the question on
     * screen: it is cashed by the credit it named (D-935), so once honoured the
     * night is repaired and has no near miss at all. A yes that was stranded by
     * a later absence taking its credit (D-937) is about a different class, so
     * it is not shown here either.
     */
    answer?: {
      state: 'Waiting' | 'Declined'
      teacherName?: string | null
      on?: string | null
      reason?: string | null
    } | null
  } | null
}

/**
 * How many misses the percentage is actually reflecting.
 *
 * Derived from the standing, not from the list: the list is capped and an
 * excused absence is not counted against her, so counting rows would disagree
 * with the figure it sits beneath — which is the confusion this whole panel was
 * rebuilt to remove.
 */
export function missedCount(standing: AttendanceStanding): number {
  return Math.max(0, standing.counted - standing.attended)
}

/**
 * A dancer's sizes, as her own family sees them (D-238).
 *
 * <b>`notes` is deliberately not here.</b> It is the studio's working note
 * against a measurement, written without a parent reading over the shoulder.
 * Showing it would change what Tonya feels able to write.
 */
export type PortalMeasurements = {
  tShirt: string | null
  leotard: string | null
  pant: string | null
  shoe: string | null
  tights: string | null
  height: string | null
  waist: string | null
  girth: string | null
  /** Labelled Bust on screen — the word the studio uses. */
  chest: string | null
  inseam: string | null
  /** Labelled Hip on screen. */
  hips: string | null
  measuredOn: string | null
  /** Nothing on file, so the screen says so rather than showing eleven blanks. */
  isEmpty: boolean
}

/**
 * The one size a family may change for their own child (D-238).
 *
 * Body measurements are taken with a tape at the studio; a parent's estimate of
 * a girth is how a costume arrives that does not fit on recital night. A t-shirt
 * size is the one figure the family knows better, and the one they are buying.
 */
export async function setTShirtSize(dancerId: string, size: string | null): Promise<void> {
  const res = await fetch(`/api/portal/dancer/${dancerId}/tshirt`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ size }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? `Could not save that (${res.status})`)
  }
}

export async function fetchPortalHome(): Promise<PortalHome> {
  const res = await fetch('/api/portal')
  if (!res.ok) throw new Error(`Could not load your account (${res.status})`)
  return res.json()
}

/**
 * One dancer's schedule, optionally for a named window (D-176).
 *
 * Without dates this is the next fortnight, which is what the home screen asks
 * for. The schedule passes an explicit week so a parent can move through her
 * season - the server clamps the range, so a hand-edited URL cannot ask a 10 DTU
 * database for a decade.
 */
export async function fetchPortalDancer(
  dancerId: string,
  from?: string,
  to?: string,
): Promise<PortalDancerView> {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)

  const query = params.toString()
  const res = await fetch(`/api/portal/dancer/${dancerId}${query ? '?' + query : ''}`)
  if (!res.ok) throw new Error(`Could not load their schedule (${res.status})`)
  return res.json()
}

/** One session a family could turn up to, and the absence it would repair. */
export type MakeupCandidate = {
  occurrenceId: string
  className: string
  date: string
  startTime: string | null
  roomName: string | null
  instructors: string[]
  repairsOccurrenceId: string
  repairsClassName: string
  /**
   * Every absence this session can **still** repair, oldest first (D-340).
   *
   * `repairsOccurrenceId` above is only the first of these — a label, so a row
   * has a line to print. Filtering candidates by it answers for one absence and
   * finds nothing for every other, which is how a screen could say "nothing
   * matches this class" about a class that matches perfectly (D-209).
   *
   * "Still" is load-bearing: an absence with a booking standing against it is
   * subtracted before this list is built.
   */
  repairsOccurrenceIds: string[]
  /**
   * A one-off Tonya scheduled, rather than a class already running (D-279).
   *
   * The two differ in the one way a parent notices: a one-off has to reach a
   * minimum before it runs, and a class already running does not.
   */
  isOneOff: boolean
  signups: number
  minimumSignups: number | null
  /** Derived every time it is read - there is no scheduler (D-279). */
  isConfirmed: boolean
  stillNeeded: number
  /**
   * One level above the class she missed, so a teacher has to agree (D-403).
   *
   * The card must not read this as a refusal: *"ask your teacher"* and
   * *"nothing matches"* are different sentences, and printing the second for
   * the first is how a family stops looking (D-209).
   */
  needsApproval: boolean
}

/**
 * What a family may book to repair what their dancer has missed (D-276).
 *
 * `missed` is deliberately not `candidates.length`. An absence in a class with
 * no peer at another level counts against her and can never be offered, so a
 * window can hold nothing while she is still owed two — "nothing this week" and
 * "you are owed nothing" are different sentences.
 */
export type MakeupOffer = {
  missed: number
  booked: number
  available: number
  candidates: MakeupCandidate[]

  /**
   * Absences that are now too late to make up (D-653).
   *
   * Only the company team has a deadline, so this is 0 for everybody else.
   * Where there is one, `missed` counts what she may still book and this
   * counts what she may not — a window that merely shrank `missed` would tell
   * a parent her daughter missed fewer classes than she did.
   */
  tooLate?: number
}

export async function fetchMakeupOffer(
  dancerId: string,
  from?: string,
  to?: string,
): Promise<MakeupOffer> {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)

  const query = params.toString()
  const res = await fetch(`/api/portal/dancer/${dancerId}/makeup${query ? '?' + query : ''}`)
  if (!res.ok) throw new Error(`Could not check for makeup classes (${res.status})`)
  return res.json()
}

/**
 * A session she can attend now and spend against a later absence (D-611).
 *
 * Separate from a makeup candidate, and deliberately: every field of one exists
 * to name the absence it repairs, and banking repairs nothing yet. A parent
 * with a clean record gets nothing from the makeup offer, correctly, and
 * everything from this.
 */
export type BankableSession = {
  occurrenceId: string
  className: string
  date: string
  startTime: string | null
  roomName: string | null
  instructors: string[]
  signups: number
  minimumSignups: number | null
  /** Above her own level, so a teacher has to agree first (D-403). */
  needsApproval: boolean
  alreadyBooked: boolean
}

export async function fetchBankableSessions(
  dancerId: string,
  from?: string,
  to?: string,
): Promise<BankableSession[]> {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)

  const query = params.toString()
  const res = await fetch(`/api/portal/dancer/${dancerId}/bank${query ? '?' + query : ''}`)
  if (!res.ok) throw new Error(`Could not check for classes to bank (${res.status})`)
  return res.json()
}

/**
 * Take a place on one, against no absence (D-611).
 *
 * One id, because there is no absence to name — which is the whole difference
 * from {@link bookMakeup} and the reason the two are separate calls rather than
 * one with a nullable argument.
 */
export async function bankSession(dancerId: string, occurrenceId: string): Promise<MakeupBooked> {
  const res = await fetch(`/api/portal/dancer/${dancerId}/bank`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ occurrenceId }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? 'Could not book that. Please try again.')
  }

  return bookedOf(await res.json().catch(() => null))
}

/**
 * What a booking hands back (D-630): the teacher's question, when the class
 * was above her level and one was raised (D-403), so a conversation that
 * opened this door can pin it; null when nobody needed asking.
 */
export type MakeupBooked = { askId: string | null }

function bookedOf(body: unknown): MakeupBooked {
  const askId = (body as { askId?: string | null } | null)?.askId
  return { askId: typeof askId === 'string' ? askId : null }
}

/**
 * The missed sessions a private lesson may repair (D-405): the solos and
 * duets, in the shape the private-lesson form takes. One derivation for the
 * portal and the conversation's door (A-17).
 */
export function repairableOf(
  missed: MissedSession[],
): { occurrenceId: string; className: string; date: string }[] {
  return missed
    .filter((m) => m.repairedByPrivate)
    .map((m) => ({ occurrenceId: m.occurrenceId, className: m.className, date: m.date }))
}

/**
 * Book one of the offered sessions (D-278).
 *
 * **Both ids, always.** A makeup credits the class that was missed rather than
 * the one she sat in, so a booking that names only the session it attends is
 * unbookable — the server re-checks the pair against its own offer and never
 * trusts either half from the screen.
 */
export async function bookMakeup(
  dancerId: string,
  occurrenceId: string,
  repairsOccurrenceId: string,
): Promise<MakeupBooked> {
  const res = await fetch(`/api/portal/dancer/${dancerId}/makeup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ occurrenceId, repairsOccurrenceId }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? 'Could not book that. Please try again.')
  }

  return bookedOf(await res.json().catch(() => null))
}

/** Give the seat back. Open until the class starts, and not after (D-81). */
/**
 * A family calls off its own private lesson (D-438, D-493).
 *
 * **The route shipped with eleven tests and nothing ever called it** — endpoint
 * tests pass perfectly against a screen that does not exist, which is how a
 * whole slice of a spec went missing without a single red run.
 *
 * The refusals carry the weight here, so the message is shown rather than
 * swallowed: inside a day it names the teacher and says she can still call it
 * off, which is the conversation the bound exists to cause.
 */
export async function cancelPrivate(bookingId: string): Promise<void> {
  const res = await fetch(`/api/portal/bookings/${bookingId}/cancel`, { method: 'POST' })

  if (res.ok) return

  const problem = await res.json().catch(() => null)

  throw new Error(
    problem?.error ?? 'That lesson could not be called off. Please speak to the studio.',
  )
}

export async function cancelMakeup(dancerId: string, occurrenceId: string): Promise<void> {
  const res = await fetch(
    `/api/portal/dancer/${dancerId}/makeup/${occurrenceId}/cancel`,
    { method: 'POST' },
  )

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? 'Could not cancel that. Please try again.')
  }
}

export type AbsenceReason = 'Illness' | 'FamilyConflict' | 'SchoolEvent' | 'Other'

export const ABSENCE_REASONS: { value: AbsenceReason; label: string }[] = [
  { value: 'Illness', label: 'Illness' },
  { value: 'FamilyConflict', label: 'Family conflict' },
  { value: 'SchoolEvent', label: 'School event' },
  { value: 'Other', label: 'Other' },
]

/**
 * A single line about standing, in words rather than a bare number.
 *
 * A percentage on its own invites the wrong reading in both directions — 84%
 * looks like a failing grade to a parent used to school marks, and a rec family
 * has no rule to be measured against at all.
 */
export function standingSummary(standing: AttendanceStanding): string {
  if (standing.percentage === null) return 'No classes yet this season'
  if (!standing.isGated) return `${standing.attended} of ${standing.counted} classes attended`

  /*
   * **Says the rule, not the shortfall** (D-388).
   *
   * Reported as *"it should say above 80% required not below"*. The old wording
   * — *"Below the 80% required"* — is factually right at 0%, and it was being
   * read as a statement of the rule, where it parses as *below-80% is
   * required*. Naming what she needs to reach says the same thing and cannot
   * be read backwards.
   *
   * **And the number comes from settings now.** It was the literal `80`, which
   * the field beside it forbids in as many words: *"The percentage required,
   * from settings. Never assumed by a screen (D-168)."* Tonya can change the
   * threshold, and this badge would have gone on claiming 80 — a screen
   * confidently naming a rule that is no longer the rule.
   */
  return standing.meetsThreshold
    ? 'Good standing'
    : `Needs ${standing.threshold}% or above`
}

/**
 * What to say when there is no percentage yet (D-311).
 *
 * **The panel used to render nothing at all here**, which is a fall-through
 * rather than a decision: a family whose only session so far was an excused
 * absence saw an "Away" badge on the row and then no attendance panel, with
 * nothing to say why it was missing.
 *
 * **"No classes yet this season" is wrong for exactly that family**, and wrong
 * in the way that matters — she *has* had a class, she told the studio she would
 * miss it, and an excused absence is deliberately left out of the count
 * (PRD §7.6). Telling her nothing has happened contradicts the row above it,
 * which is D-209's rule about a missing lookup rendering as a statement about
 * the data.
 *
 * So the reason is named. Nothing here implies a threshold: a rec dancer is
 * measured and never gated, and a bar or a verdict would invent a rule.
 */
export function nothingCountedYet(standing: AttendanceStanding): string {
  const parts: string[] = []

  if (standing.excused === 1) parts.push('1 excused absence')
  else if (standing.excused > 1) parts.push(`${standing.excused} excused absences`)

  if (standing.notYetMarked === 1) parts.push('1 class not yet marked by the studio')
  else if (standing.notYetMarked > 1)
    parts.push(`${standing.notYetMarked} classes not yet marked by the studio`)

  if (parts.length > 0) return `Nothing counted yet — ${parts.join(', ')}.`

  // The grace week (D-591): classes have run and none of them count yet. "No
  // classes yet this season" would be false, and a parent who was there on
  // Tuesday knows it.
  if (standing.countsFrom && standing.countsFrom > today())
    return `Attendance counts from ${shortDate(standing.countsFrom)}.`

  // Nothing excused and nothing awaiting a register: either the season has not
  // started or every session was cancelled. Both are honestly "not yet".
  return standing.notHeld > 0
    ? 'Nothing counted yet — no classes have been held.'
    : 'No classes yet this season.'
}

/**
 * The gap disclosure that keeps the number honest (D-118).
 *
 * A percentage computed over registers nobody filled in is confidently wrong,
 * and saying so is better than picking a silent default.
 */
export function standingCaveat(standing: AttendanceStanding): string | null {
  if (!standing.hasGaps) return null

  return standing.notYetMarked === 1
    ? '1 class not yet marked by the studio'
    : `${standing.notYetMarked} classes not yet marked by the studio`
}

/**
 * Groups a dancer's sessions by date.
 *
 * Parents think in evenings — "Tuesday she has two" — not in classes.
 */
export function byDate(sessions: PortalSession[]): { date: string; sessions: PortalSession[] }[] {
  const days: { date: string; sessions: PortalSession[] }[] = []

  for (const session of sessions) {
    const day = days.find((d) => d.date === session.date)
    if (day) day.sessions.push(session)
    else days.push({ date: session.date, sessions: [session] })
  }

  return days
}

/**
 * A dancer who is in nothing this season, in the words the portal uses (D-176).
 *
 * **An empty row is true and reads as broken**, which is the whole reason this
 * sentence exists: a family opening the app in August sees nothing and reports
 * the app rather than the fact. Naming why turns an apparent fault into
 * information.
 *
 * **One sentence, because two screens say it** (D-393). It used to be the middle
 * branch of `emptyReason`, which the Today panel could not call — that takes a
 * whole `PortalDancerView` and the panel holds a name — so D-384 restated it by
 * hand and the studio had two copies of one line.
 *
 * **The rest of `emptyReason` is deleted rather than kept.** Removing
 * `EveryoneHome` in D-388 left it with no caller at all, and its other two
 * branches have no reader either — the Today row names its own next date. A
 * tested function nothing calls is the shape `CLAUDE.md` warns about: green
 * tests over a feature that is gone.
 */
export function notInAnyClasses(firstName: string): string {
  // Emilia is real: a sibling in the switcher who takes nothing this season.
  // An empty row would read as a fault rather than a fact.
  return `${firstName} is not in any classes this season.`
}

/**
 * The same fact, told to the dancer herself (D-572).
 *
 * <b>Not <c>notInAnyClasses(name)</c> with her own name in it.</b> A dancer
 * with her own login reads her own row, and a row headed <i>You are dancing</i>
 * fed through the function above produced <i>"You are dancing is not in any
 * classes this season."</i> - the heading passed where a first name was
 * expected. Two sentences rather than one substitution, because the grammar
 * differs and a name is not a pronoun.
 */
export function youAreNotInAnyClasses(): string {
  return 'You are not in any classes this season.'
}


/**
 * The three ways a parent can look at her week, in the order they are offered.
 *
 * **One list, because there were two and they disagreed.** The toggle offered
 * all three while the URL reader whitelisted `week` and `month` — so tapping
 * *Day* wrote `?view=day`, the reader rejected it, and the screen snapped back
 * to week. A control that visibly does nothing, and it shipped because the
 * second copy was written a week after the first and never compared (D-339).
 */
export const SCHEDULE_VIEWS = ['week', 'day', 'month'] as const

export type ScheduleView = (typeof SCHEDULE_VIEWS)[number]

/** Whether a hand-edited `?view=` is one this screen can actually render. */
export function isScheduleView(value: string | null): value is ScheduleView {
  return value !== null && (SCHEDULE_VIEWS as readonly string[]).includes(value)
}

/**
 * The date range a schedule view asks the server for, and what to call it
 * (D-176).
 *
 * Pure, and separated from the component because it is the part that can be
 * quietly wrong: an off-by-one at a month boundary shows a parent the wrong
 * fortnight and nothing on screen says so.
 *
 * **Month asks for the whole grid, not the calendar month** — six rows, so it
 * includes the neighbouring days the grid displays. Fetching only the 1st to the
 * 31st would leave those trailing cells blank when they do have classes, which
 * reads as "no class that day" rather than "not this month".
 *
 * Weeks start Monday, matching how the studio runs and how attendance counts a
 * week (D-170).
 */
export function scheduleWindow(
  view: ScheduleView,
  anchor: string,
): { from: string; to: string; label: string } {
  if (view === 'day') {
    return { from: anchor, to: anchor, label: formatFullDate(anchor) }
  }

  if (view === 'month') {
    const { year, month } = monthOf(anchor)
    const grid = monthGrid(year, month)

    return {
      from: grid[0],
      to: grid[grid.length - 1],
      label: new Date(`${anchor}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    }
  }

  const start = startOfWeek(anchor)
  const end = addDays(start, 6)

  return { from: start, to: end, label: formatRange(start, end) }
}

/** Moves the anchor by one unit of whatever is on screen. */
export function stepAnchor(view: ScheduleView, anchor: string, direction: 1 | -1): string {
  if (view === 'day') return addDays(anchor, direction)
  if (view === 'week') return addDays(anchor, 7 * direction)

  /*
   * Months are not a fixed number of days, so this goes through the calendar
   * rather than adding 30. Anchored to the 1st, because stepping from the 31st
   * would skip February entirely.
   */
  const { year, month } = monthOf(anchor)
  const next = addMonths(year, month, direction)

  return `${next.year}-${String(next.month).padStart(2, '0')}-01`
}

export type AffectedSession = {
  occurrenceId: string
  dancerId: string
  dancerName: string
  className: string
  date: string
  startTime: string | null
  roomName: string | null
  /** Already marked or excused. Shown and skipped, never silently re-written. */
  alreadyHandled: string | null
}

export type BulkAbsenceResult = {
  sessions: AffectedSession[]
  recorded: number
  applied: boolean
}

/**
 * One absence, however many classes it touches (D-177).
 *
 * `apply: false` describes; `true` does. Same call, same window, so what a
 * parent confirms on screen is literally the work that happens — the shape the
 * import already uses (D-91).
 */
export async function reportBulkAbsence(input: {
  from: string
  to: string
  dancerIds: string[]
  reason: AbsenceReason
  note: string | null
  /** One note for the whole absence, not one per class (D-178). */
  documentMediaAssetId: string | null
  /** The sessions confirmed on screen. Empty means all of them (D-181). */
  occurrenceIds: string[]
  apply: boolean
}): Promise<BulkAbsenceResult> {
  const res = await fetch('/api/portal/absences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? 'That could not be recorded. Please call the studio.')
  }

  return res.json()
}

/**
 * How many sessions a confirmation will actually excuse.
 *
 * Separated because the count a parent reads has to exclude the ones being
 * skipped — telling her "7 classes" and then recording 5 is the kind of small
 * dishonesty that costs trust in everything else on the screen.
 */
export function excusable(sessions: AffectedSession[]): AffectedSession[] {
  return sessions.filter((s) => s.alreadyHandled === null)
}

/**
 * Uploads a doctor's note and returns the id to attach (D-178).
 *
 * The only upload a parent can reach — the media library is staff-only, so this
 * is its own route with its own allow-list rather than a relaxation of that one.
 */
export async function uploadAbsenceDocument(file: File): Promise<string> {
  const body = new FormData()
  body.append('file', file)

  const res = await fetch('/api/portal/documents', { method: 'POST', body })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? 'That file could not be attached.')
  }

  const result: { mediaAssetId: string } = await res.json()
  return result.mediaAssetId
}

/**
 * The part of a Jackrabbit class name worth reading in a list (D-181).
 *
 * `COMP_MT | TH 430 | 9/10 | MUSICAL THEATER | LARGE | NOVICE | VICTORIA`
 * becomes `COMP_MT`. Everything after the first pipe is the studio's own
 * annotation — day, time, ages, size, level, teacher — and fourteen of those
 * stacked in a confirmation box is a wall nobody reads, which defeats the point
 * of asking somebody to check it.
 *
 * **Safe here specifically because the row already carries the rest.** Elsewhere
 * the distinguishing part of these names is at the *end*, so clipping removes
 * the answer — but a confirmation line is grouped under one dancer and shows the
 * date and time beside it, which is what tells two of her classes apart.
 */
export function shortClassName(name: string): string {
  const head = name.split('|')[0].trim()
  return head.length > 0 ? head : name
}

/** Sessions grouped by whose they are, in the order the dancers were given. */
export function byDancer(sessions: AffectedSession[]): { name: string; sessions: AffectedSession[] }[] {
  const groups: { name: string; sessions: AffectedSession[] }[] = []

  for (const session of sessions) {
    const group = groups.find((g) => g.name === session.dancerName)
    if (group) group.sessions.push(session)
    else groups.push({ name: session.dancerName, sessions: [session] })
  }

  return groups
}

/**
 * One person on the account, and whether their family sees them (D-381).
 */
export type DancerVisibility = {
  dancerId: string
  firstName: string
  name: string
  visible: boolean
  /**
   * Live enrolments — shown to the person deciding, never acted on.
   *
   * "No classes" cannot mean "hide": until a season starts every dancer in the
   * studio has none, so the obvious rule would empty the portal each September.
   */
  classes: number
}

export async function fetchDancerVisibility(): Promise<DancerVisibility[]> {
  const res = await fetch('/api/portal/dancers/visibility')
  if (!res.ok) throw new Error(`Could not load who is on your account (${res.status})`)
  return res.json()
}

export async function setDancerVisibility(dancerId: string, visible: boolean): Promise<void> {
  const res = await fetch(`/api/portal/dancers/${dancerId}/visibility`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visible }),
  })

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
}

/**
 * One dancer, and how far her own login has got (D-539).
 */
export type DancerLogin = {
  dancerId: string
  firstName: string
  name: string
  ageYears: number
  email: string | null

  /**
   * `none` | `invited` | `active` | `withdrawn`.
   *
   * **Four states rather than a boolean**, because the two middle ones need
   * different words on the screen: an invitation that has not been accepted is
   * waiting on her, and a withdrawn login is waiting on her parent.
   */
  state: string

  /** Her parent is asked first - every login, since messaging arrived (D-629). */
  needsConsent: boolean
  /** A login exists, and what was agreed no longer covers what the app does (D-629). */
  needsReconsent: boolean
  /** The terms the live consent does not carry, for a login that exists. */
  missingTerms: string[]

  /**
   * She may arrange her own days — say she will be out, claim a makeup, bank a
   * class (D-920).
   *
   * **Off by default and granted per dancer** (D-921): `asked`, *"it
   * definitely be off by default."*
   */
  mayArrangeAttendance: boolean

  /**
   * She may ask a teacher for a private lesson (D-920) — the one that costs
   * the household money, which is why it is a switch of its own.
   */
  mayBookPrivates: boolean
}

/**
 * Turn one of her two switches on or off (D-921).
 *
 * ⚠ **The name is ours, not a capability's.** The server maps `attendance`
 * and `privates` onto the two it will write and refuses anything else, so a
 * parent cannot reach the permission model through a door built for two
 * switches.
 */
export async function setDancerPermission(
  dancerId: string,
  permission: 'attendance' | 'privates',
  allowed: boolean,
): Promise<void> {
  const res = await fetch(`/api/portal/dancers/${dancerId}/permissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ permission, allowed }),
  })

  if (res.ok) return

  const problem = await res.json().catch(() => null)
  throw new Error(problem?.error ?? `Could not change that (${res.status})`)
}

/** One block of the wording, the version she is agreeing to, and the terms it covers. */
export type ConsentBlock = {
  key: string
  version: number
  body: string
  terms: string[]
}

/**
 * What a parent is asked to agree to: one block per term family, in reading
 * order (D-629). `again` says the login exists and only a new term is asked.
 */
export type DancerLoginConsent = {
  blocks: ConsentBlock[]
  terms: string[]
  again: boolean
}

/** The versions she was shown, by block key - what the server checks agreement against. */
export function versionsOf(consent: DancerLoginConsent): Record<string, number> {
  return Object.fromEntries(consent.blocks.map((b) => [b.key, b.version]))
}

export async function fetchDancerLogins(): Promise<DancerLogin[]> {
  const res = await fetch('/api/portal/dancers/logins')
  if (!res.ok) throw new Error(`Could not load who can sign in (${res.status})`)
  return res.json()
}

/**
 * The words to agree to - every block for her age on a first login, only the
 * new ones for a login that exists (D-629) - or `null` when a login exists
 * and its live consent already covers everything the app does.
 *
 * **204 is a real answer**, not an error: the endpoint is saying there is
 * nothing new to show rather than failing to show it.
 */
export async function fetchDancerLoginConsent(dancerId: string): Promise<DancerLoginConsent | null> {
  const res = await fetch(`/api/portal/dancers/${dancerId}/login/consent`)

  if (res.status === 204) return null
  if (!res.ok) throw new Error(`Could not load what you are agreeing to (${res.status})`)

  return res.json()
}

/**
 * Issue the invitation.
 *
 * **The server's own words are used when it refuses**, because it knows which
 * of several refusals this is — an address in use reads completely differently
 * from a wording that changed while she was reading it, and a generic message
 * would send her to look in the wrong place.
 */
export async function issueDancerLogin(
  dancerId: string,
  email: string,
  versions: Record<string, number> | null,
): Promise<void> {
  const res = await fetch(`/api/portal/dancers/${dancerId}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, versions }),
  })

  if (res.ok) return

  const problem = await res.json().catch(() => null)
  throw new Error(problem?.detail ?? problem?.title ?? `Could not send that (${res.status})`)
}

export async function withdrawDancerLogin(dancerId: string): Promise<void> {
  const res = await fetch(`/api/portal/dancers/${dancerId}/login/withdraw`, { method: 'POST' })
  if (!res.ok) throw new Error(`Could not switch that off (${res.status})`)
}

/**
 * Agreeing again, for a login that exists (D-629): what she agreed to no
 * longer covers what the app does, so the new words are shown and a fresh
 * consent taken. The account is untouched. The server's own words on a
 * refusal, for the same reason as the issue.
 */
export async function agreeAgain(dancerId: string, versions: Record<string, number>): Promise<void> {
  const res = await fetch(`/api/portal/dancers/${dancerId}/login/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ versions }),
  })

  if (res.ok) return

  const problem = await res.json().catch(() => null)
  throw new Error(problem?.detail ?? problem?.title ?? `Could not record that (${res.status})`)
}
